import React from 'react';
import Modal from './Modal';
import PrintPortal from './PrintPortal';
import { inr, num, dt } from '../utils/format';

const money = (n) => inr(n || 0);
const monthName = (ym) => {
  const [y, m] = String(ym || '').split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
};

// A detailed, printable payslip. `slip` holds the computed/finalized figures;
// `employee` supplies name/code/designation (optional). Draft preview or final.
const PayslipView = ({ slip, employee, onClose }) => {
  if (!slip) return null;
  const emp = employee || {};
  const deductions = [
    ['Leave / off deduction', slip.excessOffDeduction, `${slip.excessOffs || 0} excess off${slip.absentDays ? ` + ${slip.absentDays} absent` : ''}${slip.halfDays ? ` + ${slip.halfDays} half-day` : ''}`],
    ['Salary advance', slip.salaryAdvance, 'taken this month'],
    ['Items taken from branch', slip.itemsTotal, ''],
    ['Loan / advance installment', slip.loanInstallment, ''],
    ['Other deductions', slip.otherDeductions, ''],
  ].filter(([, v]) => Number(v) > 0);
  const totalDeductions = deductions.reduce((s, [, v]) => s + Number(v || 0), 0);

  // Built once, rendered twice: on screen inside the modal, and again in the print
  // portal — printing from inside the modal only ever produced the first page.
  const doc = (
    <div className="payslip-doc">
        <div className="payslip-head">
          <div>
            <div className="payslip-brand">Avakaaya Foods Pvt Ltd</div>
            <div className="payslip-sub">Kukatpally, Hyderabad, Telangana 500072</div>
          </div>
          <div className="payslip-title">
            <div>PAYSLIP</div>
            <div className="payslip-sub">{monthName(slip.month)}{slip.status === 'paid' ? '' : ' (preview)'}</div>
          </div>
        </div>

        <table className="payslip-meta">
          <tbody>
            <tr><th>Employee</th><td>{emp.name || '—'}</td><th>Code</th><td>{emp.code || '—'}</td></tr>
            <tr><th>Designation</th><td>{emp.designation || '—'}</td><th>Days in month</th><td>{slip.monthDays || '—'}</td></tr>
            <tr><th>Offs taken / allowed</th><td>{num(slip.offsTaken, 0)} / {num(slip.offsAccrued, 0)}</td><th>Per-day rate</th><td>{money(slip.dailyRate)}</td></tr>
            {slip.paidAt && <tr><th>Paid on</th><td>{dt(slip.paidAt)}</td><th>Via</th><td>{slip.paidVia ? slip.paidVia.toUpperCase() : '—'}{slip.paidRef ? ` · ${slip.paidRef}` : ''}</td></tr>}
          </tbody>
        </table>

        <div className="payslip-cols">
          <div className="payslip-box">
            <div className="payslip-box-title">Earnings</div>
            <table className="payslip-lines">
              <tbody>
                <tr><td>Fixed salary</td><td className="right">{money(slip.fixedSalary)}</td></tr>
                {Number(slip.bonus) > 0 && <tr><td>Bonus / addition</td><td className="right">{money(slip.bonus)}</td></tr>}
              </tbody>
              <tfoot><tr><td>Gross</td><td className="right">{money(Number(slip.fixedSalary) + Number(slip.bonus || 0))}</td></tr></tfoot>
            </table>
          </div>
          <div className="payslip-box">
            <div className="payslip-box-title">Deductions</div>
            <table className="payslip-lines">
              <tbody>
                {deductions.length ? deductions.map(([label, v, note]) => (
                  <tr key={label}><td>{label}{note ? <span className="payslip-note"> ({note})</span> : ''}</td><td className="right">{money(v)}</td></tr>
                )) : <tr><td className="muted">No deductions</td><td className="right">—</td></tr>}
              </tbody>
              <tfoot><tr><td>Total deductions</td><td className="right">{money(totalDeductions)}</td></tr></tfoot>
            </table>
          </div>
        </div>

        <div className="payslip-net">
          <span>Net pay</span>
          <span>{money(slip.netPay)}</span>
        </div>
      <p className="payslip-foot">Computer-generated payslip — Avakaaya Foods Pvt Ltd.</p>
    </div>
  );

  return (
    <Modal title={`Payslip — ${monthName(slip.month)}`} onClose={onClose} wide
      footer={<><button className="btn btn-outline no-print" onClick={onClose}>Close</button><button className="btn btn-primary no-print" onClick={() => window.print()}>Print</button></>}>
      {doc}
      <PrintPortal>{doc}</PrintPortal>
    </Modal>
  );
};

export default PayslipView;
